import { tokens } from "@blurb/codex-react";
import { createStyles } from "@mantine/core";
import { DateRangePicker, DatePicker, type DateRangePickerValue } from "@mantine/dates";
import { FirstDayOfWeek } from "@mantine/dates/lib/types";
import dayjs from "dayjs";
import React from "react";
import { Calendar } from "tabler-icons-react";

const useDatePickerStyles = createStyles({
  calendar: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing[2],
  },
  label: {
    color: tokens.color.semantic.text.bold,
    fontFamily: tokens.font.family.sans,
    fontSize: tokens.font.size.base,
    "font-weight": tokens.font.weight.semibold,
    lineHeight: tokens.font.lineHeight.normal,
    paddingBottom: tokens.spacing[2],
  },
  input: {
    color: tokens.color.semantic.text.bold,
    fontFamily: tokens.font.family.sans,
    fontSize: tokens.font.size.base,
    lineHeight: tokens.font.lineHeight.normal,
    borderColor: tokens.color.semantic.border.subtle,

    "&:disabled": {
      backgroundColor: tokens.color.semantic.bg.neutralDisabled,
    },

    "&:hover": {
      backgroundColor: tokens.color.semantic.bg.inputHover,
    },

    "& + .mantine-Input-rightSection": {
      color: tokens.color.semantic.icon.bold,
      cursor: "auto",
    },

    "&:disabled + .mantine-Input-rightSection": {
      color: tokens.color.semantic.icon.disabled,
    },
  },
  weekday: {
    color: tokens.color.semantic.text.subtle,
  },
  day: {
    fontSize: tokens.font.size.sm,
    lineHeight: tokens.font.lineHeight.snug,
    borderRadius: "100%",
    width: tokens.spacing[8],
    height: tokens.spacing[8],
    borderWidth: 1,

    "&:hover": {
      backgroundColor: tokens.color.semantic.bg.inputHover,
    },

    "&[data-first-in-range]": {
      borderTopLeftRadius: "100%",
      borderBottomLeftRadius: "100%",
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
    },

    "&[data-last-in-range]": {
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
      borderTopRightRadius: "100%",
      borderBottomRightRadius: "100%",
    },

    "&[data-in-range]": {
      backgroundColor: tokens.color.semantic.bg.primarySubtlest,
    },

    "&, &[data-weekend]": { color: tokens.color.semantic.text.bold, "font-weight": tokens.font.weight.bold },
    "&[data-outside]": { color: tokens.color.semantic.text.subtle, "font-weight": tokens.font.weight.normal },
    "&[data-selected]": {
      backgroundColor: tokens.color.semantic.bg.primary,
      color: tokens.color.semantic.text.inverse,
    },
  },
  today: {
    color: `${tokens.color.semantic.text.primary} !important`,
    "&[data-selected]": {
      color: `${tokens.color.semantic.text.inverse} !important`,
    },
  },
});

export type DatePickerValue = (Date | null) | DateRangePickerValue;

type DatePickerInputProps = {
  label?: string;
  initialMonth?: Date;
  disabled?: boolean;
} & (
  | { range: true; value: DateRangePickerValue; onChange: (value: DateRangePickerValue) => void }
  | { range?: false; value: Date | null; onChange: (value: Date | null) => void }
);

const INPUT_FORMAT = "MM/DD/YYYY"; // i18n?

export const DatePickerInput: React.FC<DatePickerInputProps> = ({
  disabled,
  label,
  range,
  value,
  initialMonth,
  onChange,
}) => {
  const { classes } = useDatePickerStyles();

  const today = dayjs();
  const dayClassName = React.useCallback(
    (date: Date) => (dayjs(date).isSame(today, "day") ? classes.today : ""),
    [today, classes.today]
  );

  const commonProps = {
    label,
    disabled,
    inputFormat: INPUT_FORMAT,
    classNames: {
      wrapper: classes.calendar,
      label: classes.label,
      input: classes.input,
      weekday: classes.weekday,
      day: classes.day,
    },
    initialMonth: initialMonth,
    clearable: false,
    rightSection: <Calendar />,
    firstDayOfWeek: "sunday" as FirstDayOfWeek,
    dayClassName,
  };

  if (range) {
    return (
      <DateRangePicker
        {...commonProps}
        value={value}
        onChange={onChange}
        placeholder={`${INPUT_FORMAT} - ${INPUT_FORMAT}`}
      />
    );
  }

  return <DatePicker {...commonProps} value={value} onChange={onChange} placeholder={INPUT_FORMAT} />;
};
